import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { clone, deepEquals, deepMerge, exists } from '@jack-henry/frontend-utils/functions';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import {
    Observable,
    ObservationSource,
    SubscriptionHandle,
    combineMulti,
} from '@jack-henry/frontend-utils/observable';
import { NavigationService } from '@treasury/core/navigation';
import {
    getKeys,
    listenFor,
    querySlot,
    querySlotAll,
    removeArrayElement,
    type Action,
} from '@treasury/utils';
import { TemplateResult, css, html, nothing } from 'lit';
import { customElement, property, queryAssignedElements, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { TmDialogButtonPosture, TmDialogService } from '../../../services/dialog';
import { TmBaseComponent } from '../../../tm-base.component';
import type { TmButton as TmJhButton } from '../../jh-ui/tm-button';
import type { TmButton as VendorButton } from '../../tm-button';
import { TmButtonType } from '../../tm-button.types';
import { TmFormControl } from './tm-form-control';
import { getFormControls } from './tm-form-control.helpers';
import {
    FormControlValidationResult,
    TmFormControlConnected,
    TmFormControlEvent,
    getDefaultValue,
} from './tm-form-control.types';
import { buildFormValidationResult } from './tm-form.helpers';
import {
    ControlErrorDigest,
    FormChangedPayload,
    FormValidationResult,
    TmFormEvent,
    dispatchFormEvent,
    type TmFormValidator,
} from './tm-form.types';

type TmButton = VendorButton | TmJhButton;

export const tagName = 'tm-form';
/**
 * Provides a declarative, reactive form framework for `@treasury/presentation` form fields.
 *
 * @fires { TmFormChanged } form-changed - Fired when one of the inputs of the form has received a new value.
 * @fires { TmFormSubmitted } form-submitted - Fired when the submit button has been clicked and the form is valid.
 */
@customElement(tagName)
export class TmForm<T> extends TmBaseComponent {
    /**
     * Validation predicate to invoke against the form's model to determine
     * if the model meets validity requirements (i.e., business logic) beyond
     * the format constraints imposed by the form inputs.
     */
    @property({
        attribute: false,
    })
    public validator?: TmFormValidator<T>;

    /**
     * If enabled, forms that are in a dirty state will show a warning
     * dialog that prevents navigation until the user confirms they
     * want to discard their changes.
     */
    @property({
        type: Boolean,
        attribute: 'prompt-for-unsaved',
    })
    public promptForUnsaved = false;

    /**
     * Displays a line item list of all errors in the form when `true`.
     */
    @property({
        type: Boolean,
        attribute: 'show-digest',
    })
    public readonly showErrorDigest = false;

    public readonly controls = new Set<TmFormControl<unknown>>();

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private dialogService: TmDialogService;

    private cleanupTasks: Action[] = [];

    private model: Partial<T> = {};

    @state()
    private startingModel: Partial<T> = {};

    /**
     * Track the state of the model previous to the current state
     * to determine if certain comparisons need to be run again.
     */
    private previousModel: Partial<T> = {};

    /**
     * A partial model containing default values for some form fields.
     * If present, they will be used for keys that are empty in `startingModel`
     * when initializing or resetting the form.
     *
     * If the model is present, the reset button behavior
     * changes to consult the `defaultModel` rather than just the `startModel`.
     */
    private defaultModel?: Partial<T>;

    private submitBtns: TmButton[] = [];

    private clearBtn?: TmButton;

    private resetBtn?: TmButton;

    private controlsUpdated$?: Observable<TmFormControl<unknown>>;

    private controlsUpdatedSub?: SubscriptionHandle;

    private readonly buttonsSetupSource = new ObservationSource<boolean>();

    private readonly slotRef = createRef<HTMLSlotElement>();

    private hasRendered = false;

    private lastValidation?: boolean;

    /**
     * A digest of the last validation result, if the form was invalid
     * and the validator returned a `FormValidationResult`.
     */
    private lastValidationResult?: FormValidationResult<T>;

    @state()
    private errorDigestContent: TemplateResult | typeof nothing = nothing;

    @queryAssignedElements()
    private readonly slottedElements?: Element[];

    private get slotElem() {
        return this.slotRef.value;
    }

    public get buttons() {
        return [...this.submitBtns, this.resetBtn, this.clearBtn].filter(exists);
    }

    public get isPristine() {
        return deepEquals(this.model, this.startingModel);
    }

    /**
     * Similar to `isPristine` but with defaults considered.
     *
     * Is `true` if the current form state differs from the initial
     * form state with defaults filled in for empty values.
     *
     * If no defaults have been set, this will be `false`.
     */
    private get differsFromDefault() {
        if (!this.defaultModel) {
            return false;
        }

        /**
         * The `startingModel` values should take precedence
         * and only be filled in by `defaultModel` ones when missing.
         */
        const combined = deepMerge(this.defaultModel, this.startingModel);

        return !deepEquals(this.model, combined);
    }

    public get isValid() {
        // don't re-run validation logic if state hasn't changed
        if (exists(this.lastValidation) && !this.checkStateChanged()) {
            // ensure that any form-level errors are propagated even if state hasn't changed;
            // this is to accommodate the case where any initial error state calculated during slot change
            // is cleared after subsequent form control values changes
            this.assignValidationResult();
            return this.lastValidation;
        }

        return this.validate();
    }

    public disconnectedCallback(): void {
        this.cleanupTasks.forEach(unsubscribe => unsubscribe());
        this.cleanupTasks = [];
    }

    public submit(button: TmButton) {
        if (!this.isValid) {
            return;
        }

        const { model, isPristine } = this;
        dispatchFormEvent(this, TmFormEvent.FormSubmitted, {
            buttonName: button.name ?? '',
            model,
            isPristine,
        });

        // the data of a submitted form becomes the new initial model
        this.startingModel = clone(model);
        this.applyButtonStates();
    }

    protected firstUpdated() {
        this.listenForControls();
        this.addUnsavedGuards();
    }

    protected render() {
        this.trackRenderStatus();

        return html`<form>
            ${this.errorDigestContent}
            <slot ${ref(this.slotRef)} @slotchange=${() => this.onSlotChange()}
                >Empty form. Add children that implement <code>TmForm</code>.</slot
            >
        </form>`;
    }

    /**
     * Checks the current model state and the previous model state
     * to determine if a change has occurred.
     *
     * @returns `true` if the states have changed
     */
    private checkStateChanged() {
        return !deepEquals(this.previousModel, this.model);
    }

    private generateErrorDigest() {
        return Array.from(this.controls.values()).reduce(
            (digest, control) => {
                const { isValid, errorMessages } = control;

                if (!isValid) {
                    const key = control.name as keyof T;
                    digest[key] = {
                        control,
                        // no need to account for form-level errors since they are
                        // already assigned to the control prior to this using assignValidationResult()
                        errorMessages,
                    };
                }

                return digest;
            },
            {} as FormChangedPayload<T>['errors']
        );
    }

    private trackControl(control: TmFormControl<unknown>) {
        if (this.controls.has(control)) {
            return;
        }

        const name = control.name as keyof T;
        if (typeof name !== 'string' || name.length < 1) {
            throw new Error(`Could not track control with name "${name.toString()}"`);
        }

        const value = (exists(control.initial) ? control.initial : control.value) as
            | T[typeof name]
            | undefined;

        this.controls.add(control);
        this.model[name] = value;
        this.previousModel[name] = clone(value);

        if (!(name in this.startingModel)) {
            this.startingModel[name] = clone(value);
        }

        const { unsubscribe } = control.disconnected$.subscribe(() => {
            this.onControlDisconnected(control, unsubscribe);
        });

        this.cleanupTasks.push(unsubscribe);
        this.controlsUpdated$ = this.controlsUpdated$
            ? combineMulti(
                  this.controlsUpdated$,
                  control.valueChanged$.pipe(() => control)
              )
            : control.valueChanged$.pipe(() => control);

        // reset subscription since a new combined observable was created
        if (this.controlsUpdatedSub) {
            this.controlsUpdatedSub.unsubscribe();
            this.controlsUpdatedSub = undefined;
        }

        this.controlsUpdatedSub = this.controlsUpdated$.subscribe(c => this.onControlUpdated(c));
        this.trySetDefaultValue(control);
    }

    /**
     * Attempt to set the default value for a given control, if present.
     */
    private async trySetDefaultValue(control: TmFormControl<unknown>) {
        const name = control.name as keyof T;
        const defaultValue = getDefaultValue(control) as T[typeof name] | undefined;

        if (!defaultValue) {
            return;
        }

        if (!this.defaultModel) {
            this.defaultModel = {};
        }

        this.defaultModel[name] = defaultValue;

        // if there's a default value, mutate the control so that the form
        // begins in a dirty state and the submit button is enabled
        await control.changeValue(defaultValue);
    }

    private listenForControls() {
        const { unsubscribe } = listenFor(this, TmFormControlEvent.ControlConnected, e => {
            this.onControlConnected(e as TmFormControlConnected<unknown>);
        });

        this.cleanupTasks.push(unsubscribe);
    }

    /**
     * Listen for slot changes to gather references to child form controls.
     * This only accounts for controls rendered at the time of the change.
     * Controls that are added/shown at a later time must be listened for
     * using `TmFormControlEvent.ControlConnected`.
     */
    private onSlotChange() {
        if (!this.slotElem || !this.slottedElements) {
            return;
        }

        const controls = this.slottedElements.map(getFormControls).flat();
        controls.forEach(c => this.trackControl(c));

        this.setUpButtons();
    }

    private onControlConnected(event: TmFormControlConnected<unknown>) {
        event.stopImmediatePropagation();
        this.trackControl(event.detail.control);
    }

    private onControlDisconnected(control: TmFormControl<unknown>, unsubscribe: () => void) {
        const name = control.name as keyof T;

        this.controls.delete(control);
        delete this.model[name];
        delete this.startingModel[name];

        unsubscribe();
        removeArrayElement(unsubscribe, this.cleanupTasks);

        this.dispatchUpdate(TmFormEvent.FormChanged);
    }

    /**
     * Invoked when any of the forms constituent controls is updated.
     */
    private onControlUpdated(control: TmFormControl<unknown>) {
        const name = control.name as keyof T;
        this.previousModel[name] = clone(this.model[name]);
        this.model[name] = control.value as T[keyof T];
        this.applyButtonStates();
        this.dispatchUpdate(TmFormEvent.FormChanged);
    }

    private dispatchUpdate(
        event: TmFormEvent.FormChanged | TmFormEvent.FormReset | TmFormEvent.FormClear
    ) {
        // only dispatch updates after the form has been initialized and rendered for the first time
        // and the state has changed
        if (!(this.hasUpdated && this.hasRendered) || !this.checkStateChanged()) {
            return;
        }

        const { isValid, model, isPristine } = this;
        const errors = this.generateErrorDigest();
        const payload = {
            isValid,
            model,
            isPristine,
            errors,
        };

        if (this.showErrorDigest) {
            this.errorDigestContent = renderErrorDigest(errors);
        }

        dispatchFormEvent(this, event, payload);
    }

    private setUpButtons() {
        if (!this.slotElem) {
            throw new Error('Cannot wire up buttons. Invalid slot element reference.');
        }
        // Support multiple submit buttons by querying all matching elements and wiring them up
        // querySlot only returns the first matching element, so to get multiple submit buttons,
        // we need to use querySelectorAll or a similar utility.

        this.submitBtns = querySlotAll<TmButton>(
            this.slotElem,
            `tm-jh-button[type=${TmButtonType.Submit}], tm-button[type=${TmButtonType.Submit}]`
        );

        this.submitBtns.forEach(btn => {
            const { unsubscribe } = listenFor(btn, 'click', () => this.submit(btn));
            this.cleanupTasks.push(unsubscribe);
        });

        const resetBtn = querySlot<TmButton>(
            this.slotElem,
            `tm-jh-button[type=${TmButtonType.Reset}], tm-button[type=${TmButtonType.Reset}]`
        );
        if (resetBtn && resetBtn !== this.resetBtn) {
            this.resetBtn = resetBtn;
            const { unsubscribe } = listenFor(this.resetBtn, 'click', () => {
                this.onReset();
            });
            this.cleanupTasks.push(unsubscribe);
        }

        const clearBtn = querySlot<TmButton>(
            this.slotElem,
            `tm-jh-button[type=${TmButtonType.Clear}], tm-button[type=${TmButtonType.Clear}]`
        );
        if (clearBtn && clearBtn !== this.clearBtn) {
            this.clearBtn = clearBtn;
            const { unsubscribe } = listenFor(this.clearBtn, 'click', () => this.onClear());
            this.cleanupTasks.push(unsubscribe);
        }

        // TODO: change this to a void type/emit once @jack-henry/frontend-utils is updated to v3.5.3
        this.buttonsSetupSource.emit(true);
        this.applyButtonStates();
    }

    private async applyButtonStates() {
        await this.buttonsSetupSource.toPromise(true);
        const { isPristine, isValid } = this;

        if (this.submitBtns && this.submitBtns.length > 0) {
            this.submitBtns.forEach(btn => {
                btn.disabled = isPristine || !isValid;
            });
        }

        if (this.resetBtn) {
            this.resetBtn.disabled = this.defaultModel ? !this.differsFromDefault : isPristine;
        }
    }

    private validate() {
        this.lastValidationResult = undefined;
        const validatorResult = this.validator
            ? this.validator(this.model, callback =>
                  buildFormValidationResult(Array.from(this.controls), callback)
              )
            : true;
        if (validatorResult === false) {
            return (this.lastValidation = false);
        } else if (typeof validatorResult !== 'boolean') {
            this.lastValidationResult = validatorResult;
            this.assignValidationResult();
            return (this.lastValidation = false);
        }

        const validationResult = Array.from(this.controls.values())
            .map(control => control.isValid)
            // one invalid control makes the whole form invalid
            .every(isValid => isValid === true);

        this.lastValidation = validationResult;

        return validationResult;
    }

    private async onClear() {
        const clearPromises = Array.from(this.controls).map(c => c.clearValue());
        await Promise.all(clearPromises);
        await this.updateComplete;
        this.dispatchUpdate(TmFormEvent.FormClear);
    }

    private async onReset() {
        const resetPromises = Array.from(this.controls).map(control => {
            const { name } = control;
            const startValue = this.startingModel[name as keyof T];
            const defaultValue = getDefaultValue(control);
            const resetValue = exists(startValue) ? startValue : defaultValue;

            // no need to requestUpdate() here;
            // changeValue() mutates a reactive property
            return control.changeValue(resetValue);
        });

        await Promise.all(resetPromises);
        await this.updateComplete;
        this.dispatchUpdate(TmFormEvent.FormReset);
    }

    private trackRenderStatus() {
        if (!this.hasRendered) {
            this.hasRendered = true;
        }
    }

    /**
     * Assigns the last validation result to each invalid control in the form.
     *
     * This is needed in order to propagate errors originating from
     * form-level validation to the individual controls.
     */
    private assignValidationResult() {
        if (!this.lastValidationResult) {
            return;
        }

        getKeys(this.lastValidationResult).forEach(key => {
            const result = this.lastValidationResult?.[key];
            const control = Array.from(this.controls).find(c => c.name === key);
            if (!result || !control) {
                // this shouldn't happen, but best to be defensive and appease TS
                return;
            }

            control.setValidationResult(result as FormControlValidationResult<unknown>);
        });
    }

    private addUnsavedGuards() {
        if (!this.promptForUnsaved) {
            return;
        }

        const { removeGuard: removeInteractivePrompt } = this.navService.addInteractiveGuard(
            async () => {
                if (this.isPristine) {
                    return true;
                }

                const { reason } = await this.dialogService.open<void>(
                    'If you leave this view without saving, all of your changes will be lost.',
                    'You have unsaved changes',
                    {
                        buttons: [
                            {
                                label: 'Discard Changes',
                                reason: DynamicComponentExitReason.Confirm,
                                posture: TmDialogButtonPosture.Secondary,
                            },
                            {
                                label: 'Continue Editing',
                                reason: DynamicComponentExitReason.Cancel,
                                posture: TmDialogButtonPosture.Primary,
                            },
                        ],
                    }
                );

                return reason === DynamicComponentExitReason.Confirm ? true : false;
            }
        );
        const { removeGuard: removeUnloadPrompt } = this.navService.addProceduralGuard(
            () => this.isPristine
        );

        this.cleanupTasks.push(removeInteractivePrompt, removeUnloadPrompt);
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static styles = css`
        :host {
            form {
                height: var(--tm-form-height, unset);
            }

            .error-box {
                background-color: lightpink;
                border: 1px solid red;
                border-radius: 0.375rem; /* rounded-md */
                color: black;
                padding: 0.5rem; /* p-2 */
                margin-bottom: 1rem; /* mb-4 */
            }
        }
    `;
}

function renderErrorDigest<T>(digests: Record<keyof T, ControlErrorDigest<T>>) {
    const keys = getKeys(digests).filter(k => {
        const { errorMessages } = digests[k];
        return exists(errorMessages) && errorMessages.length > 0;
    });

    if (keys.length < 1) {
        return nothing;
    }

    const listItems = keys.map(key => {
        const { control, errorMessages } = digests[key];
        if (!errorMessages) {
            return nothing;
        }

        const messageItems = errorMessages.map(msg => html`<li>${msg}</li>`);
        const controlName = 'label' in control ? (control.label as string) : control.name;
        return html`<li>
            <p><strong>${controlName}:</strong></p>
            <ul class="list-disc list-inside">
                ${messageItems}
            </ul>
        </li>`;
    });

    return html`<div class="error-box">
        <ul>
            ${listItems}
        </ul>
    </div>`;
}
